import { useState, useEffect } from 'react';
import { logger } from '../../utils/logger';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, FileSpreadsheet } from 'lucide-react';
import { Button } from '../ui/button';
import {
  RoyaltyStats,
  TokenHoldersList,
  RoyaltyHistoryList,
  DistributeModal,
  TrackSelector,
  ExportButtons
} from './royalties';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminRoyalties() {
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [tokenHolders, setTokenHolders] = useState([]);
  const [royaltyHistory, setRoyaltyHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [distributing, setDistributing] = useState(false);
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  
  const [formData, setFormData] = useState({
    amount: '',
    periodStart: '',
    periodEnd: ''
  });

  useEffect(() => {
    fetchTracks();
  }, []);

  useEffect(() => {
    if (selectedTrack) {
      fetchTrackDetails(selectedTrack);
    }
  }, [selectedTrack]);

  const fetchTracks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/tracks`);
      setTracks(res.data);
    } catch (error) {
      logger.error('Failed to fetch tracks:', error);
      toast.error('Failed to load tracks');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrackDetails = async (trackId) => {
    try {
      const tokenRes = await axios.get(`${API_URL}/tokens/track/${trackId}`);
      setTokenHolders(tokenRes.data.owners || []);
      
      const royaltyRes = await axios.get(`${API_URL}/royalties/track/${trackId}`);
      setRoyaltyHistory(royaltyRes.data || []);
    } catch (error) {
      logger.error('Failed to fetch track details:', error);
    }
  };

  const handleDistribute = async () => {
    if (!selectedTrack || !formData.amount || !formData.periodStart || !formData.periodEnd) {
      toast.error('Please fill in all fields');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setDistributing(true);
    try {
      const res = await axios.post(`${API_URL}/admin/royalties/distribute`, null, {
        params: {
          track_id: selectedTrack,
          amount: amount,
          period_start: formData.periodStart,
          period_end: formData.periodEnd
        }
      });
      
      toast.success(`Successfully distributed $${amount.toFixed(2)} to ${res.data.royalty?.distributions?.length || 0} token holders! Email receipts sent.`);
      setShowDistributeModal(false);
      setFormData({ amount: '', periodStart: '', periodEnd: '' });
      fetchTrackDetails(selectedTrack);
    } catch (error) {
      logger.error('Failed to distribute royalties:', error);
      toast.error(error.response?.data?.detail || 'Failed to distribute royalties');
    } finally {
      setDistributing(false);
    }
  };

  const handleExportTrackAnalytics = async () => {
    if (!selectedTrack) return;
    try {
      const response = await axios.get(`${API_URL}/admin/export/track-analytics/${selectedTrack}`, {
        responseType: 'blob'
      });
      downloadBlob(response.data, `${selectedTrackData?.title || 'track'}_analytics.csv`);
      toast.success('Track analytics exported!');
    } catch (error) {
      logger.error('Export failed:', error);
      toast.error('Failed to export analytics');
    }
  };

  const handleExportRoyaltyHistory = async () => {
    if (!selectedTrack) return;
    try {
      const response = await axios.get(`${API_URL}/admin/export/royalty-history/${selectedTrack}`, {
        responseType: 'blob'
      });
      downloadBlob(response.data, `${selectedTrackData?.title || 'track'}_royalties.csv`);
      toast.success('Royalty history exported!');
    } catch (error) {
      logger.error('Export failed:', error);
      toast.error('Failed to export royalty history');
    }
  };

  const handleExportAllTracks = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/export/all-tracks-analytics`, {
        responseType: 'blob'
      });
      downloadBlob(response.data, 'all_tracks_analytics.csv');
      toast.success('All tracks analytics exported!');
    } catch (error) {
      logger.error('Export failed:', error);
      toast.error('Failed to export analytics');
    }
  };

  const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const selectedTrackData = tracks.find(t => t.id === selectedTrack);
  const totalOwnership = tokenHolders.reduce((sum, h) => sum + (h.tokens_owned || 0), 0);
  const totalDistributed = royaltyHistory.reduce((sum, r) => sum + (r.total_amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-royalties">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-400" />
            Royalty Distribution
          </h2>
          <p className="text-gray-400 mt-1">Distribute earnings to token holders</p>
        </div>
        <Button
          onClick={handleExportAllTracks}
          variant="outline"
          className="border-white/20"
          data-testid="export-all-btn"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Export All Tracks
        </Button>
      </div>

      {/* Track Selection */}
      <TrackSelector 
        tracks={tracks}
        selectedTrack={selectedTrack}
        onSelect={setSelectedTrack}
      />

      {selectedTrack && (
        <>
          {/* Export Buttons */}
          <ExportButtons 
            onExportTrackAnalytics={handleExportTrackAnalytics}
            onExportRoyaltyHistory={handleExportRoyaltyHistory}
          />

          {/* Track Stats */}
          <RoyaltyStats 
            tokenHolders={tokenHolders}
            totalOwnership={totalOwnership}
            totalDistributed={totalDistributed}
            royaltyHistory={royaltyHistory}
          />

          {/* Token Holders List */}
          <TokenHoldersList 
            tokenHolders={tokenHolders}
            onDistributeClick={() => setShowDistributeModal(true)}
          />

          {/* Royalty History */}
          <RoyaltyHistoryList royaltyHistory={royaltyHistory} />
        </>
      )}

      {/* Distribute Modal */}
      <DistributeModal
        open={showDistributeModal}
        onOpenChange={setShowDistributeModal}
        trackTitle={selectedTrackData?.title}
        formData={formData}
        setFormData={setFormData}
        tokenHolders={tokenHolders}
        totalOwnership={totalOwnership}
        onDistribute={handleDistribute}
        distributing={distributing}
      />
    </div>
  );
}
